import {
  UPDATE_ADMIN_SHARED_TENANT_STATUS_REQUEST,
  UPDATE_ADMIN_SHARED_TENANT_STATUS_SUCCESS,
  UPDATE_ADMIN_SHARED_TENANT_STATUS_FAILURE,
  UPDATE_ADMIN_BOOKING_LABELS_REQUEST,
  UPDATE_ADMIN_BOOKING_LABELS_SUCCESS,
  UPDATE_ADMIN_BOOKING_LABELS_FAILURE,
  UPDATE_ADMIN_BOOKING_TENANT_FEATURES_REQUEST,
  UPDATE_ADMIN_BOOKING_TENANT_FEATURES_SUCCESS,
  UPDATE_ADMIN_BOOKING_TENANT_FEATURES_FAILURE,
  FETCH_ADMIN_BOOKING_VERSION_FEATURES_REQUEST,
  FETCH_ADMIN_BOOKING_VERSION_FEATURES_SUCCESS,
  FETCH_ADMIN_BOOKING_VERSION_FEATURES_FAILURE,
  UPDATE_ADMIN_BOOKING_VERSION_FEATURES_REQUEST,
  UPDATE_ADMIN_BOOKING_VERSION_FEATURES_SUCCESS,
  UPDATE_ADMIN_BOOKING_VERSION_FEATURES_FAILURE,
  DELETE_ADMIN_BOOKING_VERSION_FEATURES_REQUEST,
  DELETE_ADMIN_BOOKING_VERSION_FEATURES_SUCCESS,
  DELETE_ADMIN_BOOKING_VERSION_FEATURES_FAILURE,
  UPDATE_ADMIN_FAULT_STATUS_REQUEST,
  UPDATE_ADMIN_FAULT_STATUS_SUCCESS,
  UPDATE_ADMIN_FAULT_STATUS_FAILURE,
  CLEAR_ADMIN_SHARED_TENANT_MUTATION_ERRORS,
  UPDATE_ADMIN_SHARED_TENANT_EDIT_REQUEST,
  UPDATE_ADMIN_SHARED_TENANT_EDIT_SUCCESS,
  UPDATE_ADMIN_SHARED_TENANT_EDIT_FAILURE,
  ONBOARD_ADMIN_SHARED_TENANT_REQUEST,
  ONBOARD_ADMIN_SHARED_TENANT_SUCCESS,
  ONBOARD_ADMIN_SHARED_TENANT_FAILURE,
  RESET_ADMIN_SHARED_TENANT_PASSWORDS_REQUEST,
  RESET_ADMIN_SHARED_TENANT_PASSWORDS_SUCCESS,
  RESET_ADMIN_SHARED_TENANT_PASSWORDS_FAILURE,
  RUN_ADMIN_SHARED_TENANT_CLEANUP_REQUEST,
  RUN_ADMIN_SHARED_TENANT_CLEANUP_SUCCESS,
  RUN_ADMIN_SHARED_TENANT_CLEANUP_FAILURE,
  DELETE_ADMIN_SHARED_TENANT_REQUEST,
  DELETE_ADMIN_SHARED_TENANT_SUCCESS,
  DELETE_ADMIN_SHARED_TENANT_FAILURE,
  RELEASE_ADMIN_SHARED_TENANT_REQUEST,
  RELEASE_ADMIN_SHARED_TENANT_SUCCESS,
  RELEASE_ADMIN_SHARED_TENANT_FAILURE,
} from '../../../../Constants/actionTypes';
import {
  updateAdminSharedTenantStatus,
  updateAdminSharedTenant,
  createAdminSharedTenant,
  updateAdminBookingLabels,
  updateAdminBookingTenantFeatures,
  getAdminBookingVersionFeatures,
  upsertAdminBookingVersionFeatures,
  deleteAdminBookingVersionFeatures,
  updateAdminFaultStatus,
  resetAdminSharedTenantPasswords,
  runAdminSharedTenantCleanup,
  deleteAdminSharedTenant,
  releaseBookingTenant,
} from '../../../../Constants/apiService';
import {
  buildStatusPayload,
  buildOnboardTenantPayload,
  buildReleaseTenantPayload,
} from '../../../../../Pages/Dashboard/Admin/Tenants/tenantHelpers';

const getNetworkErrorMessage = (error, fallback) => (
  error?.message === 'Failed to fetch' || error?.message?.includes('NetworkError')
    ? fallback
    : error?.message || fallback
);

const getStatusErrorMessage = (error) => {
  const message = error?.message || '';
  if (error?.status === 400 && /active booking|booked/i.test(message)) {
    return 'Unable to change status because this tenant has an active booking.';
  }
  if (error?.status === 404) return 'Tenant could not be found.';
  if (error?.status >= 500) return 'Unable to change tenant status. Please try again.';
  return getNetworkErrorMessage(error, 'Unable to change tenant status. Please try again.');
};

const getLabelsErrorMessage = (error) => {
  if (error?.status === 400) return 'Invalid label update.';
  if (error?.status >= 500) return 'Unable to update labels. Please try again.';
  return getNetworkErrorMessage(error, 'Unable to update labels. Please try again.');
};

const getTenantFeaturesErrorMessage = (error) => {
  const message = error?.message || '';
  if (error?.status === 400) {
    return message && !/^invalid/i.test(message) ? message : 'Invalid tenant features update.';
  }
  if (error?.status === 404) return 'Tenant could not be found.';
  if (error?.status >= 500) return 'Unable to update tenant features. Please try again.';
  return getNetworkErrorMessage(error, 'Unable to update tenant features. Please try again.');
};

const getFaultErrorMessage = (error) => {
  const message = error?.message || '';
  if (error?.status === 400 && /no such booking/i.test(message)) {
    return 'The booking state changed and this action is no longer available. Refreshing tenant data.';
  }
  if (error?.status >= 500) return 'Unable to update fault status. Please try again.';
  return getNetworkErrorMessage(error, 'Unable to update fault status. Please try again.');
};

const getEditErrorMessage = (error) => {
  const message = error?.message || '';
  if (error?.status === 400 && /active booking|booked/i.test(message)) {
    return 'This tenant cannot be edited while it has an active booking.';
  }
  if (error?.status === 400 && /maintenance|upgrade|edit/i.test(message)) {
    return 'Tenant configuration can only be edited while in Maintenance or Under Upgrade.';
  }
  if (error?.status === 400 && /field/i.test(message)) {
    return 'Some fields could not be updated.';
  }
  if (error?.status === 400) {
    return getNetworkErrorMessage(error, 'Unable to update tenant. Please check the entered values.');
  }
  if (error?.status === 404) return 'Tenant could not be found.';
  if (error?.status >= 500) return 'Unable to update tenant. Please try again.';
  return getNetworkErrorMessage(error, 'Unable to update tenant. Please try again.');
};

const getOnboardErrorMessage = (error) => {
  const message = error?.message || '';
  if (error?.status === 400 && /already exists|duplicate/i.test(message)) {
    return 'Tenant already exists.';
  }
  if (error?.status === 409) return 'Tenant onboarding is already in progress.';
  if (error?.status === 503) return 'Tenant onboarding is temporarily unavailable. Please try again later.';
  if (error?.status === 400) {
    return getNetworkErrorMessage(error, 'Unable to onboard tenant. Please check the entered values.');
  }
  if (error?.status >= 500) return 'Unable to onboard tenant. Please try again.';
  return getNetworkErrorMessage(error, 'Unable to onboard tenant. Please try again.');
};

const getPasswordResetErrorMessage = (error) => {
  if (error?.status >= 500) return 'Unable to reset passwords. Please try again.';
  return getNetworkErrorMessage(error, 'Unable to reset passwords. Please try again.');
};

const getCleanupErrorMessage = (error) => {
  if (error?.status >= 500) return 'Unable to run shared tenant cleanup. Please try again.';
  return getNetworkErrorMessage(error, 'Unable to run shared tenant cleanup. Please try again.');
};

const getDeleteTenantErrorMessage = (error) => {
  const message = error?.message || '';
  if (error?.status === 400 && /active booking|booked/i.test(message)) {
    return 'Unable to delete this tenant because it has an active booking.';
  }
  if (error?.status === 404) return 'Tenant could not be found.';
  if (error?.status >= 500) return 'Unable to delete tenant. Please try again.';
  return getNetworkErrorMessage(error, 'Unable to delete tenant. Please try again.');
};

const getVersionFeaturesErrorMessage = (error, fallback) => {
  const message = error?.message || '';
  if (error?.status === 400) {
    return message && !/^invalid/i.test(message) ? message : fallback;
  }
  if (error?.status >= 500) return fallback;
  return getNetworkErrorMessage(error, fallback);
};

const getReleaseTenantErrorMessage = (error) => {
  const message = error?.message || '';
  if (error?.status === 400) {
    return 'This booking can no longer be released. Tenant data will be refreshed.';
  }
  if (error?.status === 404 || /no such booking/i.test(message)) {
    return 'The booking or tenant could not be found.';
  }
  if (error?.status === 401 || error?.status === 403) {
    return getNetworkErrorMessage(error, 'You do not have permission to release this tenant.');
  }
  if (error?.status >= 500) return 'Unable to release tenant. Please try again.';
  return getNetworkErrorMessage(error, 'Unable to release tenant. Please try again.');
};

export const updateAdminSharedTenantStatusAct = (tenant, newDisplayStatus) => async (dispatch, getState) => {
  dispatch({ type: UPDATE_ADMIN_SHARED_TENANT_STATUS_REQUEST });

  const accessToken = getState()?.auth?.accessToken;
  const payload = buildStatusPayload(tenant, newDisplayStatus);

  try {
    const data = await updateAdminSharedTenantStatus(accessToken, payload);
    dispatch({ type: UPDATE_ADMIN_SHARED_TENANT_STATUS_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const errorMessage = getStatusErrorMessage(error);
    dispatch({ type: UPDATE_ADMIN_SHARED_TENANT_STATUS_FAILURE, payload: errorMessage });
    throw new Error(errorMessage);
  }
};

export const updateAdminBookingLabelsAct = (payload) => async (dispatch, getState) => {
  dispatch({ type: UPDATE_ADMIN_BOOKING_LABELS_REQUEST });

  const accessToken = getState()?.auth?.accessToken;

  try {
    const data = await updateAdminBookingLabels(accessToken, payload);
    dispatch({ type: UPDATE_ADMIN_BOOKING_LABELS_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const errorMessage = getLabelsErrorMessage(error);
    dispatch({ type: UPDATE_ADMIN_BOOKING_LABELS_FAILURE, payload: errorMessage });
    throw new Error(errorMessage);
  }
};

export const updateAdminBookingTenantFeaturesAct = (payload) => async (dispatch, getState) => {
  dispatch({ type: UPDATE_ADMIN_BOOKING_TENANT_FEATURES_REQUEST });

  const accessToken = getState()?.auth?.accessToken;

  try {
    const data = await updateAdminBookingTenantFeatures(accessToken, payload);
    dispatch({ type: UPDATE_ADMIN_BOOKING_TENANT_FEATURES_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const errorMessage = getTenantFeaturesErrorMessage(error);
    dispatch({ type: UPDATE_ADMIN_BOOKING_TENANT_FEATURES_FAILURE, payload: errorMessage });
    throw new Error(errorMessage);
  }
};

export const fetchAdminBookingVersionFeaturesAct = () => async (dispatch, getState) => {
  dispatch({ type: FETCH_ADMIN_BOOKING_VERSION_FEATURES_REQUEST });

  const accessToken = getState()?.auth?.accessToken;

  try {
    const data = await getAdminBookingVersionFeatures(accessToken);
    const list = Array.isArray(data?.versionFeaturesByLabel) ? data.versionFeaturesByLabel : [];
    dispatch({ type: FETCH_ADMIN_BOOKING_VERSION_FEATURES_SUCCESS, payload: list });
    return list;
  } catch (error) {
    const errorMessage = getVersionFeaturesErrorMessage(
      error,
      'Unable to load version features. Please try again.'
    );
    dispatch({ type: FETCH_ADMIN_BOOKING_VERSION_FEATURES_FAILURE, payload: errorMessage });
    throw new Error(errorMessage);
  }
};

export const upsertAdminBookingVersionFeaturesAct = (payload) => async (dispatch, getState) => {
  dispatch({ type: UPDATE_ADMIN_BOOKING_VERSION_FEATURES_REQUEST });

  const accessToken = getState()?.auth?.accessToken;

  try {
    const data = await upsertAdminBookingVersionFeatures(accessToken, payload);
    dispatch({ type: UPDATE_ADMIN_BOOKING_VERSION_FEATURES_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const errorMessage = getVersionFeaturesErrorMessage(
      error,
      'Unable to save version features. Please try again.'
    );
    dispatch({ type: UPDATE_ADMIN_BOOKING_VERSION_FEATURES_FAILURE, payload: errorMessage });
    throw new Error(errorMessage);
  }
};

export const deleteAdminBookingVersionFeaturesAct = (payload) => async (dispatch, getState) => {
  dispatch({ type: DELETE_ADMIN_BOOKING_VERSION_FEATURES_REQUEST });

  const accessToken = getState()?.auth?.accessToken;

  try {
    const data = await deleteAdminBookingVersionFeatures(accessToken, payload);
    dispatch({ type: DELETE_ADMIN_BOOKING_VERSION_FEATURES_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const errorMessage = getVersionFeaturesErrorMessage(
      error,
      'Unable to delete version features. Please try again.'
    );
    dispatch({ type: DELETE_ADMIN_BOOKING_VERSION_FEATURES_FAILURE, payload: errorMessage });
    throw new Error(errorMessage);
  }
};

export const updateAdminFaultStatusAct = (payload) => async (dispatch, getState) => {
  dispatch({ type: UPDATE_ADMIN_FAULT_STATUS_REQUEST });

  const accessToken = getState()?.auth?.accessToken;

  try {
    const data = await updateAdminFaultStatus(accessToken, payload);
    dispatch({ type: UPDATE_ADMIN_FAULT_STATUS_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const errorMessage = getFaultErrorMessage(error);
    dispatch({ type: UPDATE_ADMIN_FAULT_STATUS_FAILURE, payload: errorMessage });
    const err = new Error(errorMessage);
    err.shouldRefetch = /booking state changed/i.test(errorMessage);
    throw err;
  }
};

export const updateAdminSharedTenantEditAct = (payload) => async (dispatch, getState) => {
  dispatch({ type: UPDATE_ADMIN_SHARED_TENANT_EDIT_REQUEST });

  const accessToken = getState()?.auth?.accessToken;

  try {
    const data = await updateAdminSharedTenant(accessToken, payload);
    dispatch({ type: UPDATE_ADMIN_SHARED_TENANT_EDIT_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const errorMessage = getEditErrorMessage(error);
    dispatch({ type: UPDATE_ADMIN_SHARED_TENANT_EDIT_FAILURE, payload: errorMessage });
    throw new Error(errorMessage);
  }
};

export const onboardAdminSharedTenantAct = (formData) => async (dispatch, getState) => {
  dispatch({ type: ONBOARD_ADMIN_SHARED_TENANT_REQUEST });

  const accessToken = getState()?.auth?.accessToken;
  const payload = buildOnboardTenantPayload(formData);

  try {
    const data = await createAdminSharedTenant(accessToken, payload);
    dispatch({ type: ONBOARD_ADMIN_SHARED_TENANT_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const errorMessage = getOnboardErrorMessage(error);
    dispatch({ type: ONBOARD_ADMIN_SHARED_TENANT_FAILURE, payload: errorMessage });
    throw new Error(errorMessage);
  }
};

export const resetAdminSharedTenantPasswordsAct = (payload) => async (dispatch, getState) => {
  dispatch({ type: RESET_ADMIN_SHARED_TENANT_PASSWORDS_REQUEST });

  const accessToken = getState()?.auth?.accessToken;

  try {
    const data = await resetAdminSharedTenantPasswords(accessToken, payload);
    dispatch({ type: RESET_ADMIN_SHARED_TENANT_PASSWORDS_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const errorMessage = getPasswordResetErrorMessage(error);
    dispatch({ type: RESET_ADMIN_SHARED_TENANT_PASSWORDS_FAILURE, payload: errorMessage });
    throw new Error(errorMessage);
  }
};

export const runAdminSharedTenantCleanupAct = () => async (dispatch, getState) => {
  dispatch({ type: RUN_ADMIN_SHARED_TENANT_CLEANUP_REQUEST });

  const accessToken = getState()?.auth?.accessToken;

  try {
    const data = await runAdminSharedTenantCleanup(accessToken);
    dispatch({ type: RUN_ADMIN_SHARED_TENANT_CLEANUP_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const errorMessage = getCleanupErrorMessage(error);
    dispatch({ type: RUN_ADMIN_SHARED_TENANT_CLEANUP_FAILURE, payload: errorMessage });
    throw new Error(errorMessage);
  }
};

export const deleteAdminSharedTenantAct = (payload) => async (dispatch, getState) => {
  dispatch({ type: DELETE_ADMIN_SHARED_TENANT_REQUEST });

  const accessToken = getState()?.auth?.accessToken;

  try {
    const data = await deleteAdminSharedTenant(accessToken, payload);
    dispatch({ type: DELETE_ADMIN_SHARED_TENANT_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const errorMessage = getDeleteTenantErrorMessage(error);
    dispatch({ type: DELETE_ADMIN_SHARED_TENANT_FAILURE, payload: errorMessage });
    throw new Error(errorMessage);
  }
};

export const releaseAdminSharedTenantAct = (tenant) => async (dispatch, getState) => {
  dispatch({ type: RELEASE_ADMIN_SHARED_TENANT_REQUEST });

  const accessToken = getState()?.auth?.accessToken;
  const payload = buildReleaseTenantPayload(tenant);

  try {
    const data = await releaseBookingTenant(accessToken, payload);
    dispatch({ type: RELEASE_ADMIN_SHARED_TENANT_SUCCESS, payload: data });
    return data;
  } catch (error) {
    const errorMessage = getReleaseTenantErrorMessage(error);
    dispatch({ type: RELEASE_ADMIN_SHARED_TENANT_FAILURE, payload: errorMessage });
    const err = new Error(errorMessage);
    err.shouldRefetch = error?.status === 400 || error?.status === 404;
    throw err;
  }
};

export const clearAdminSharedTenantMutationErrorsAct = () => ({
  type: CLEAR_ADMIN_SHARED_TENANT_MUTATION_ERRORS,
});
